<template>
    <div class="smsmallform">
        <div class="header">
            <h1 v-if="parameters?.isEditing">Redigera sms-mall</h1>
            <h1 v-else-if="parameters?.isDuplicating">Duplicera sms-mall</h1>
            <h1 v-else>Skapa sms-mall</h1>

            <div>
                <div>
                    <strong>Typ av mall</strong><br />
                    <span>Operation</span>
                </div>
                <br />
                <div>
                    <KForm
                        id="smsmall-form"
                        @submit="submitForm"
                        @cancel="cancel"
                        :initial-values-getter="initForm"
                        submitContent="Spara"
                        cancelContent="Avbryt"
                    >
                        <div class="form-content">
                            <KText
                                class="span-all"
                                name="namn"
                                label="Namn på mall *"
                                suffixText="Max 40 tecken"
                                validation="required"
                                :validation-messages="{
                                    required: 'Namn på mall är obligatoriskt',
                                }"
                            />
                            <AdvancedEditor
                                class="span-all"
                                simple-editor
                                :content="content"
                                @update:content="updateContent"
                            />
                        </div>
                    </KForm>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    export default {
        name: "SMSMallForm",
        tabName: "Skapa | Redigera sms-mall",
    };
</script>

<script setup lang="ts">
    import { onMounted, ref, toRefs } from "vue";
    import { useStore } from "@/store";
    import { getConfig, klinikenApi } from "@/api";
    import { flashMessage, openDialog } from "@/utils";
    import tabEvents from "@/tabEvents";
    import { getFraser } from "@/components/Editor/helpers";
    import AdvancedEditor from "@/components/Editor/AdvancedEditor.vue";
    import KForm from "@/components/forms/Form/KForm.vue";

    const props = defineProps<{
        tabId: number;
        parameters?: Record<string, any>;
    }>();

    const { parameters, tabId } = toRefs(props);
    const store = useStore();

    const errors = ref<Record<any, any> | null>(null);
    const options = ref([]);
    const content = ref("");

    if (parameters?.value?.isEditing) {
        store.commit("tabs/setTabName", { id: tabId.value, name: "Redigera sms-mall" });
    } else if (parameters?.value?.isDuplicating) {
        store.commit("tabs/setTabName", { id: tabId.value, name: "Duplicera sms-mall" });
    } else {
        store.commit("tabs/setTabName", { id: tabId.value, name: "Skapa sms-mall" });
    }

    content.value = parameters?.value?.body ?? "";

    const initForm = async () => ({
        namn: parameters?.value?.namn ?? "",
    });

    const updateContent = ({ data, errors: contentErrors }: { data: string; errors: Map<any, any> }) => {
        content.value = data;
        errors.value = contentErrors;
    };

    const submitForm = async (formData: any = {}) => {
        if (errors.value?.size) {
            openDialog(Array.from(errors.value.values()).flat().join(","), "error");

            return;
        }
        formData.typ = "operation";
        formData.body = content.value;

        await store.dispatch("tabs/displaySpinner", { id: tabId.value, display: true });

        try {
            let response: Record<string, any> = {};

            if (parameters?.value?.id) {
                const id = parameters.value.id;

                response = await klinikenApi.put(`/sms/mallar/${id}/`, formData, getConfig());
            } else {
                response = await klinikenApi.post("/sms/mallar/", formData, getConfig());
            }

            if (response.status === 201 || response.status === 200) {
                tabEvents.$emit("refreshSmsMallar", {});
                flashMessage(response.status === 201 ? "Sms-mall skapad" : "Sms-mall redigerad");
                await store.dispatch("tabs/closeTab", tabId.value);
            }
        } catch (error: any) {
            openDialog(error.response.data["namn"][0] ?? "", "error");
        } finally {
            await store.dispatch("tabs/displaySpinner", { id: tabId.value, display: false });
        }
    };

    const cancel = async () => {
        await store.dispatch("tabs/closeTab", tabId.value);
    };

    onMounted(async () => {
        // TODO Använder funktionen från editor dir pga att sms kommer använda editorn i framtiden
        options.value = await getFraser();
    });
</script>

<style lang="scss" scoped>
    @import "@/style/container";

    #smsmall-form {
        width: $content-width;
    }
    .smsmallform {
        position: relative;

        ::v-deep(.formulate-input) {
            .formulate-input-help {
                &:before {
                    content: url("@/assets/info_gray.svg");
                    margin-right: 5px;
                    position: relative;
                    bottom: -2px;
                }
            }
        }
    }
</style>
